import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { AdminButton, Badge, Card, CostValue, DataTable, FilterBar, FilterChip, FilterCombobox, formatMoney, InfoHint, LabelWithInfo, SectionHeader, Select, } from '../admin-ui';
import { useAdminVoiceProviders } from '../voice-admin-api';
import { featureGroups, labelFor, modeGroups, providerGroups, } from './filter-groups';
import { useUsageMeta, useUsageOperations, useInterviewEconomics, useUsageSummary, } from './ai-usage-api';
import InterviewCostDrawer from './interview-cost-drawer';
import { phaseStyle } from './phase-theme';
import { ProviderCell } from './usage-charts';
import UsageRecordDrawer from './usage-record-drawer';
/**
 * Usage & cost console.
 *
 * REPORT SEMANTICS (the reason several things here look fussier than a normal
 * dashboard): the three states below must never be shown as the same thing.
 *
 *   not metered   no records exist — the platform was not measuring
 *   unpriced      measured, but no rate is configured
 *   priced        a real figure
 *
 * Rendering the first two as "$0.00" would assert that nothing was spent, which
 * is a different and much more damaging claim than "we don't know yet".
 *
 * The layout answers questions in the order an operator asks them: what are we
 * spending → is it going up → what does one interview cost → who is spending it
 * → on what → and finally the individual calls.
 */
const GROUP_LABELS = {
    feature: 'Feature',
    model: 'Model',
    provider: 'Provider',
    service: 'Service',
    mode: 'Interview mode',
    organization: 'Organization',
    recruiter: 'Recruiter',
    interview: 'Interview',
    phase: 'Interview stage',
    metric: 'Usage type',
    date: 'Date',
};
// Every grouping the API offers needs an entry here AND in GROUP_LABELS: the
// dropdown is built from the server's list, so a grouping missing from these
// falls back to the feature column and renders the wrong data under the wrong
// heading rather than failing visibly.
const DIMENSION_FIELDS = {
    feature: ['feature'],
    model: ['provider', 'model'],
    provider: ['provider'],
    service: ['provider', 'service'],
    mode: ['interview_mode'],
    organization: ['organization_id'],
    recruiter: ['organization_id', 'recruiter_id'],
    interview: ['interview_slug'],
    phase: ['phase'],
    metric: ['metric_key'],
    date: ['date'],
};
const MODE_LABELS = {
    realtime: 'Realtime',
    cost_optimized: 'Cost-optimized',
    pre_interview: 'Pre-interview',
    unknown: 'Unknown',
};
const METRIC_HEADERS = {
    input_tokens: 'Input',
    cached_input_tokens: 'Cached',
    output_tokens: 'Output',
    total_tokens: 'Total',
};
/** What each column actually means. Tertiary detail: reachable from the header
 *  without occupying a line of the table on every read. */
const COLUMN_HELP = {
    feature: 'The platform capability responsible for this usage.',
    provider: 'External AI or voice service used for this usage.',
    model: 'The specific model the provider ran.',
    service: 'The kind of processing — language model, speech-to-text, text-to-speech.',
    interview_mode: 'How the interview was processed.',
    interview_slug: 'The individual interview this usage belongs to.',
    organization_id: 'The customer organization this spend belongs to.',
    recruiter_id: 'The recruiter whose action generated this usage.',
    metric_key: 'The meter the provider bills on — input tokens, characters, audio seconds.',
    phase: 'When in the interview’s life the spend happened — before it, during it, or after it.',
    date: 'The day the usage was recorded.',
    operations: 'Number of provider/API calls recorded.',
    quantity: 'Total tokens recorded for this usage.',
    cost: 'Calculated provider cost for the recorded usage.',
};
const num = (v) => {
    const n = typeof v === 'string' ? Number(v) : v;
    return Number.isFinite(n) ? n : 0;
};
/** Shared with CostValue so a tile and a table cell never disagree about the
 *  same number — and so neither can round a real cost down to $0. */
const money = (n) => formatMoney(n);
export default function UsagePanel({ view = 'breakdown', onOpenInterview, } = {}) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    const [groupBy, setGroupBy] = useState('feature');
    const [provider, setProvider] = useState('');
    const [mode, setMode] = useState('');
    const [feature, setFeature] = useState('');
    const [grain, setGrain] = useState('day');
    const [split, setSplit] = useState('none');
    const [chartMetric, setChartMetric] = useState('cost_usd');
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [selectedInterview, setSelectedInterview] = useState(null);
    const { data: meta } = useUsageMeta();
    const filters = {
        provider: provider || undefined,
        mode: mode || undefined,
        feature: feature || undefined,
    };
    // Each sub-section fetches ONLY what it shows. Loading all three on every
    // usage screen would triple the queries for data two of them never render.
    // Which providers are VOICE providers comes from the registry the scheduling
    // flow already uses — derived, not a hardcoded list, so a newly registered
    // provider groups itself.
    const { data: voiceProviders } = useAdminVoiceProviders();
    const voiceIds = useMemo(() => (voiceProviders !== null && voiceProviders !== void 0 ? voiceProviders : []).map((p) => p.provider_id), [voiceProviders]);
    const providerOpts = useMemo(() => providerGroups(meta === null || meta === void 0 ? void 0 : meta.providers, voiceIds), [meta, voiceIds]);
    const modeOpts = useMemo(() => modeGroups(meta), [meta]);
    const featureOpts = useMemo(() => featureGroups(meta), [meta]);
    /** Two real examples, so the dimension means something before it is used. */
    const sample = (groups) => groups
        .flatMap((g) => g.options)
        .filter((o) => o.value)
        .slice(0, 2)
        .map((o) => o.label)
        .join(', ');
    const providerExample = sample(providerOpts);
    const modeExample = sample(modeOpts);
    const featureExample = sample(featureOpts);
    /** States what the table below is actually built from — counted off the real
     *  option sets, never a fabricated figure. */
    const breakdownContext = useMemo(() => {
        const providerCount = providerOpts.flatMap((g) => g.options).filter((o) => o.value).length;
        const parts = [
            provider ? `provider ${labelFor(providerOpts, provider)}` : `all ${providerCount} providers`,
            mode ? `${labelFor(modeOpts, mode)} interviews` : 'all interview modes',
            feature ? labelFor(featureOpts, feature) : 'all features',
        ];
        return `Showing ${parts.join(' · ')}.`;
    }, [provider, mode, feature, providerOpts, modeOpts, featureOpts]);
    const summary = useUsageSummary(Object.assign({ group_by: groupBy }, filters), view === 'breakdown');
    const operations = useUsageOperations(Object.assign({ limit: 50 }, filters), view === 'events');
    const economics = useInterviewEconomics({ provider: provider || undefined, mode: mode || undefined }, view === 'interviews');
    // Raw feature keys mean nothing to an operator, so rows and filters both show
    // the human name with the explanation on hover.
    const featureInfo = useMemo(() => {
        var _a;
        const map = new Map();
        ((_a = meta === null || meta === void 0 ? void 0 : meta.features) !== null && _a !== void 0 ? _a : []).forEach((f) => map.set(f.key, { label: f.label || f.key, description: f.description }));
        return map;
    }, [meta]);
    const phaseInfo = useMemo(() => {
        var _a;
        const map = new Map();
        ((_a = meta === null || meta === void 0 ? void 0 : meta.phases) !== null && _a !== void 0 ? _a : []).forEach((p) => map.set(p.key, { label: p.label, description: p.description }));
        return map;
    }, [meta]);
    // ── Cost breakdown ──────────────────────────────────────────────────
    const summaryColumns = useMemo(() => {
        var _a;
        const dims = (_a = DIMENSION_FIELDS[groupBy]) !== null && _a !== void 0 ? _a : ['feature'];
        return [
            ...dims.map((field) => ({
                key: field,
                header: (_jsxs("span", { className: 'inline-flex items-center', children: [field.replace(/_/g, ' '), COLUMN_HELP[field] && _jsx(InfoHint, { text: COLUMN_HELP[field] })] })),
                render: (row) => {
                    var _a, _b;
                    const raw = String((_a = row[field]) !== null && _a !== void 0 ? _a : '—');
                    const info = field === 'feature' ? featureInfo.get(raw) : undefined;
                    if (info) {
                        return _jsx(LabelWithInfo, { label: info.label, description: info.description, sub: raw });
                    }
                    // Phase keys are dotted-free but still internal (`in_interview`), and
                    // the phase's own explanation is the most useful thing about it.
                    if (field === 'phase') {
                        const meaning = phaseInfo.get(raw);
                        return (_jsxs("span", { className: 'flex min-w-0 items-center gap-1.5', children: [_jsx("span", { className: 'h-2.5 w-2.5 flex-shrink-0 rounded-[3px]', style: { background: phaseStyle(raw).color }, "aria-hidden": 'true' }), _jsx(LabelWithInfo, { label: (_b = meaning === null || meaning === void 0 ? void 0 : meaning.label) !== null && _b !== void 0 ? _b : raw.replace(/_/g, ' '), description: meaning === null || meaning === void 0 ? void 0 : meaning.description, normalCase: true })] }));
                    }
                    // An id is not a name. The record already carries both, so show the
                    // name and keep the id underneath for anyone cross-referencing.
                    const name = field === 'organization_id' ? row.organization_name
                        : field === 'recruiter_id' ? row.recruiter_name
                            : null;
                    if (name) {
                        return (_jsxs("span", { className: 'flex min-w-0 flex-col', children: [_jsx("span", { className: 'truncate font-medium text-dash-text', children: name }), _jsx("span", { className: 'truncate text-[11px] text-dash-muted', children: raw })] }));
                    }
                    return (_jsx("span", { className: 'font-medium capitalize text-dash-text', children: raw.replace(/_/g, ' ') }));
                },
            })),
            {
                key: 'provider',
                header: (_jsxs("span", { className: 'inline-flex items-center', children: ["Provider", _jsx(InfoHint, { text: COLUMN_HELP.provider })] })),
                hideOnMobile: true,
                render: (row) => (
                // Answers "whose service was this" — the question a collapsed
                // "Speech-to-Text" row could not. A row spanning two providers shows
                // BOTH rather than picking one.
                _jsx(ProviderCell, { provider: row.provider, providers: row.providers })),
            },
            {
                key: 'operations',
                header: (_jsxs("span", { className: 'inline-flex items-center', children: ["Calls", COLUMN_HELP.operations && _jsx(InfoHint, { text: COLUMN_HELP.operations })] })),
                align: 'right',
                hideOnMobile: true,
                render: (row) => {
                    var _a;
                    return (_jsx("span", { className: 'tabular-nums text-dash-text2', children: ((_a = row.operations) !== null && _a !== void 0 ? _a : 0).toLocaleString() }));
                },
            },
            {
                key: 'quantity',
                header: (_jsxs("span", { className: 'inline-flex items-center', children: ["Tokens", COLUMN_HELP.quantity && _jsx(InfoHint, { text: COLUMN_HELP.quantity })] })),
                align: 'right',
                render: (row) => (_jsx("span", { className: 'tabular-nums text-dash-text2', children: num(row.quantity).toLocaleString() })),
            },
            {
                key: 'cost',
                header: (_jsxs("span", { className: 'inline-flex items-center', children: ["Cost (USD)", COLUMN_HELP.cost && _jsx(InfoHint, { text: COLUMN_HELP.cost })] })),
                align: 'right',
                render: (row) => (_jsx(CostValue, { amount: row.cost_usd, unpricedCount: row.unpriced_records })),
            },
        ];
    }, [groupBy, featureInfo, phaseInfo]);
    // ── Calls ───────────────────────────────────────────────────────────
    const metricColumns = ((_b = (_a = operations.data) === null || _a === void 0 ? void 0 : _a.columns) !== null && _b !== void 0 ? _b : ['input_tokens', 'cached_input_tokens', 'output_tokens']).map((metric) => {
        var _a;
        return ({
            key: metric,
            header: (_a = METRIC_HEADERS[metric]) !== null && _a !== void 0 ? _a : metric.replace(/_/g, ' '),
            align: 'right',
            render: (r) => {
                const m = r.metrics[metric];
                if (!m)
                    return _jsx("span", { className: 'text-dash-muted', children: "\u2014" });
                return (_jsxs("span", { className: 'inline-flex flex-col items-end leading-tight', children: [_jsx("span", { className: 'tabular-nums text-dash-text', children: num(m.quantity).toLocaleString() }), m.pricing_status !== 'unpriceable' && (_jsx("span", { className: 'text-[10.5px] tabular-nums text-dash-muted', children: _jsx(CostValue, { amount: m.cost_reporting, unpricedCount: m.pricing_status === 'unpriced' ? 1 : 0 }) }))] }));
            },
        });
    });
    const operationColumns = [
        {
            key: 'time',
            header: 'Time',
            render: (r) => (_jsx("span", { className: 'whitespace-nowrap text-dash-muted', children: new Date(r.timestamp).toLocaleString() })),
        },
        {
            key: 'feature',
            header: 'Feature',
            render: (r) => {
                const info = featureInfo.get(r.feature);
                return _jsx(LabelWithInfo, { label: (info === null || info === void 0 ? void 0 : info.label) || r.feature, description: info === null || info === void 0 ? void 0 : info.description });
            },
        },
        {
            key: 'model',
            header: 'Model',
            render: (r) => {
                var _a;
                return (_jsx("span", { className: 'text-dash-text2', children: (_a = r.model) !== null && _a !== void 0 ? _a : _jsx("span", { className: 'italic text-dash-muted', children: "unbound" }) }));
            },
        },
        ...metricColumns,
        {
            key: 'cost',
            header: 'Call cost',
            align: 'right',
            render: (r) => (_jsx(CostValue, { amount: r.cost_reporting, unpricedCount: r.unpriced_records })),
        },
    ];
    // Prefer the console's drawer when one is provided, so the same interview
    // opens the same way from every screen.
    const openInterview = (slug) => onOpenInterview ? onOpenInterview(slug) : setSelectedInterview(slug);
    const interviewColumns = [
        {
            key: 'interview',
            header: 'Interview',
            render: (r) => (_jsxs("span", { className: 'flex min-w-0 flex-col', children: [_jsx("span", { className: 'truncate font-medium text-dash-text', children: r.interview_slug }), _jsx("span", { className: 'truncate text-[11px] text-dash-muted', children: r.last_seen ? new Date(r.last_seen).toLocaleString() : '—' })] })),
        },
        {
            key: 'mode',
            header: 'Mode',
            render: (r) => {
                var _a;
                return (_jsx(Badge, { tone: r.interview_mode === 'realtime' ? 'purple' : 'cyan', children: (_a = MODE_LABELS[r.interview_mode]) !== null && _a !== void 0 ? _a : r.interview_mode }));
            },
        },
        {
            key: 'recruiter',
            header: 'Recruiter',
            hideOnMobile: true,
            render: (r) => {
                var _a, _b;
                return (_jsxs("span", { className: 'flex min-w-0 flex-col', children: [_jsx("span", { className: 'truncate text-dash-text2', children: (_a = r.recruiter_name) !== null && _a !== void 0 ? _a : '—' }), _jsx("span", { className: 'truncate text-[11px] text-dash-muted', children: (_b = r.organization_name) !== null && _b !== void 0 ? _b : '' })] }));
            },
        },
        {
            key: 'calls',
            header: 'Calls',
            align: 'right',
            hideOnMobile: true,
            render: (r) => _jsx("span", { className: 'tabular-nums text-dash-text2', children: r.operations }),
        },
        {
            key: 'cost',
            header: 'Cost',
            align: 'right',
            render: (r) => _jsx(CostValue, { amount: r.cost_usd, unpricedCount: r.unpriced_records }),
        },
    ];
    const filtered = provider || mode || feature;
    return (_jsxs("div", { className: 'space-y-4', children: [_jsxs(Card, { children: [_jsxs("div", { className: 'mb-3 flex flex-wrap items-start justify-between gap-2', children: [_jsxs("div", { className: 'min-w-0', children: [_jsx("h2", { className: 'text-[14px] font-semibold text-dash-text', children: "Usage & Cost" }), _jsx("p", { className: 'mt-0.5 text-[12px] text-dash-muted', children: "Analyze platform spend across providers, interview modes and product features. Select a dimension to drill into the underlying usage." })] }), _jsx(HowThisWorks, {})] }), _jsxs(FilterBar, { children: [_jsx(FilterCombobox, { label: 'Provider', helper: 'Who provides the AI or voice service', example: providerExample, value: provider, onChange: setProvider, groups: providerOpts }), _jsx(FilterCombobox, { label: 'Interview mode', helper: 'How the interview was processed', example: modeExample, value: mode, onChange: setMode, groups: modeOpts }), _jsx(FilterCombobox, { label: 'Feature', helper: 'Which platform capability generated the usage', example: featureExample, value: feature, onChange: setFeature, groups: featureOpts })] }), _jsxs("div", { className: 'mt-3 flex flex-wrap items-center gap-1.5 border-t border-dash-border pt-3', children: [_jsx("span", { className: 'text-[11.5px] text-dash-muted', children: "Showing:" }), _jsx(FilterChip, { label: 'Provider', value: labelFor(providerOpts, provider), onRemove: provider ? () => setProvider('') : undefined }), _jsx(FilterChip, { label: 'Mode', value: labelFor(modeOpts, mode), onRemove: mode ? () => setMode('') : undefined }), _jsx(FilterChip, { label: 'Feature', value: labelFor(featureOpts, feature), onRemove: feature ? () => setFeature('') : undefined }), filtered && (_jsx(AdminButton, { onClick: () => {
                                    setProvider('');
                                    setMode('');
                                    setFeature('');
                                }, children: "Clear all" }))] })] }), view === 'breakdown' && (_jsxs(Card, { children: [_jsx(SectionHeader, { title: 'Cost breakdown', subtitle: breakdownContext, action: _jsx(Select, { label: 'Group breakdown by', value: groupBy, onChange: setGroupBy, options: ((_c = meta === null || meta === void 0 ? void 0 : meta.group_by) !== null && _c !== void 0 ? _c : ['feature']).map((g) => {
                                var _a;
                                return ({
                                    value: g,
                                    label: (_a = GROUP_LABELS[g]) !== null && _a !== void 0 ? _a : g,
                                });
                            }) }) }), _jsx("p", { className: '-mt-1 mb-2 text-[11px] text-dash-muted', children: "Choose how usage and cost are grouped in the table below. Unpriced events are counted, never summed into the total." }), _jsx(DataTable, { columns: summaryColumns, rows: (_e = (_d = summary.data) === null || _d === void 0 ? void 0 : _d.rows) !== null && _e !== void 0 ? _e : [], rowKey: (r) => { var _a; return ((_a = DIMENSION_FIELDS[groupBy]) !== null && _a !== void 0 ? _a : ['feature']).map((f) => String(r[f])).join('|'); }, loading: summary.isLoading, empty: 'No usage matches these filters.', 
                        // Grouped by interview, a row IS an interview — open its full
                        // breakdown rather than making the operator go find it.
                        onRowClick: groupBy === 'interview'
                            ? (r) => { var _a; return openInterview(String((_a = r.interview_slug) !== null && _a !== void 0 ? _a : '')); }
                            : undefined })] })), view === 'interviews' && (_jsxs(Card, { children: [_jsx(SectionHeader, { title: 'Interviews by cost', subtitle: 'Most expensive first. An interview with an unpriced record is shown but left out of the mode averages.' }), _jsx(DataTable, { columns: interviewColumns, rows: (_g = (_f = economics.data) === null || _f === void 0 ? void 0 : _f.interviews) !== null && _g !== void 0 ? _g : [], rowKey: (r) => r.interview_slug, onRowClick: (r) => openInterview(r.interview_slug), loading: economics.isLoading, empty: 'No interview usage measured yet.' })] })), view === 'events' && (_jsxs(Card, { children: [_jsx(SectionHeader, { title: 'Usage events', subtitle: 'One row per API call, newest first. Select a row for the full record and its pricing basis.' }), _jsx(DataTable, { columns: operationColumns, rows: (_j = (_h = operations.data) === null || _h === void 0 ? void 0 : _h.rows) !== null && _j !== void 0 ? _j : [], rowKey: (r) => r.operation_key, 
                        // The drawer is per RECORD, so open the call's first metric — that is
                        // where the shared request id, attempts and raw payload live.
                        onRowClick: (r) => { var _a; return setSelectedRecord((_a = r.usage_ids[0]) !== null && _a !== void 0 ? _a : null); }, loading: operations.isLoading, empty: 'No usage events yet.' }), ((_l = (_k = operations.data) === null || _k === void 0 ? void 0 : _k.total) !== null && _l !== void 0 ? _l : 0) > ((_o = (_m = operations.data) === null || _m === void 0 ? void 0 : _m.rows.length) !== null && _o !== void 0 ? _o : 0) && (_jsxs("div", { className: 'mt-2 text-center text-[11.5px] text-dash-muted', children: ["Showing ", (_p = operations.data) === null || _p === void 0 ? void 0 : _p.rows.length, " of", ' ', (_r = (_q = operations.data) === null || _q === void 0 ? void 0 : _q.total) === null || _r === void 0 ? void 0 : _r.toLocaleString(), " calls"] }))] })), _jsx(UsageRecordDrawer, { usageId: selectedRecord, onClose: () => setSelectedRecord(null) }), _jsx(InterviewCostDrawer, { interviewSlug: selectedInterview, onClose: () => setSelectedInterview(null) })] }));
}
/**
 * Progressive disclosure for the vocabulary.
 *
 * The definitions matter — an admin cannot read a cost table without knowing
 * what "Provider" or "Calls" mean here — but a permanent help panel would take
 * space from the data on every visit, for something read once.
 */
function HowThisWorks() {
    const [open, setOpen] = useState(false);
    const DEFINITIONS = [
        ['Provider', 'Who performed the AI or voice processing.'],
        ['Mode', 'How the interview was processed.'],
        ['Feature', 'Which platform capability generated the usage.'],
        ['Calls', 'Number of recorded provider calls.'],
        ['Tokens', 'Token usage, where the provider bills on tokens.'],
        ['Cost', 'Calculated provider cost for the recorded usage.'],
    ];
    return (_jsxs("div", { className: 'relative flex-shrink-0', children: [_jsxs(AdminButton, { onClick: () => setOpen((v) => !v), children: [_jsx(Info, { className: 'h-3.5 w-3.5' }), "How this works"] }), open && (_jsxs(_Fragment, { children: [_jsx("div", { className: 'fixed inset-0 z-20', onClick: () => setOpen(false), "aria-hidden": 'true' }), _jsxs("div", { role: 'dialog', "aria-label": 'How Usage and Cost works', className: 'absolute right-0 top-[38px] z-30 w-[min(340px,85vw)] rounded-lg border border-dash-border bg-dash-bg2 p-3 shadow-xl', children: [_jsx("p", { className: 'mb-2 text-[12.5px] font-semibold text-dash-text', children: "How Usage & Cost works" }), _jsx("p", { className: 'mb-2 text-[11.5px] leading-snug text-dash-muted', children: "Spend is calculated from recorded provider usage \u2014 nothing is estimated." }), _jsx("dl", { className: 'space-y-1', children: DEFINITIONS.map(([term, meaning]) => (_jsxs("div", { className: 'flex gap-2 text-[11.5px]', children: [_jsx("dt", { className: 'w-[52px] flex-shrink-0 font-medium text-dash-text2', children: term }), _jsx("dd", { className: 'min-w-0 text-dash-muted', children: meaning })] }, term))) })] })] }))] }));
}
